import React from "react";
import { Box, Grid, Typography, TextField, IconButton, Button, Switch, MenuItem } from "@mui/material";
import { CheckCircle, Cancel, Add, Delete, Clear } from "@mui/icons-material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import VirtualizedAutocomplete from "../../../../../../components/virtualization/virtualizedAutocomplete";
import {
	CampoCliente,
	renderOpcaoCliente,
} from "../../../../../../components/opcaoCliente";
import { getClienteOptionLabel } from "../../../../../../components/grupoEconomico/helpers";

export default function BasicConfigSection({
	theme,
	styles,
	nomeRelatorio,
	setNomeRelatorio,
	isAgendamentoEnabled,
	setIsAgendamentoEnabled,
	frequenciaAgendamento,
	setFrequenciaAgendamento,
	agendamentoDataInicial,
	setAgendamentoDataInicial,
	emails,
	handleAddEmail,
	handleRemoveEmail,
	handleEmailChange,
	obterTextoAgendamento,
	clientes = [],
	selectedCliente = null,
	setSelectedCliente,
}) {
	return (
		<Box
			sx={{
				p: 2,
				bgcolor: theme.palette.background.paper,
				borderRadius: "12px",
				width: "100%",
				mb: 1,
			}}
		>
			{/* Cabeçalho da seção */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					width: "100%",
					flexWrap: "wrap",
					gap: 2,
					mb: 2,
				}}
			>
				<Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "16px" }}>
					Configurações Básicas
				</Typography>

				
			</Box>
			<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 2,
						justifyContent: "space-between",
						mb: 2,
					}}
				>
					{/* Filtro de Cliente */}
					
					<VirtualizedAutocomplete
						sx={{
							width: "280px",
							maxWidth: "100%",
						}}
						value={selectedCliente}
						onChange={(_, newValue) => {
							setSelectedCliente?.(newValue);
							if (newValue?.id) {
								const clientStorage = {
									interfaceId: "",
									clienteId: newValue?.id,
									hostId: "",
									selectTime: 5,
									initialDate: "",
									finalDate: "",
								};
								localStorage.setItem("filterData", JSON.stringify(clientStorage));
							} else {
								localStorage.removeItem("filterData");
							}
						}}
						clearIcon={
							<Clear
								fontSize="small"
								onClick={() => {
									localStorage.removeItem("filterData");
									setSelectedCliente?.(null);
								}}
							/>
						}
						size="small"
						options={(clientes || []).filter(
							(cliente) => !cliente?.isGrupoEconomico
						)}
						getOptionLabel={(option) => getClienteOptionLabel(option)}
						renderOption={renderOpcaoCliente()}
						renderInput={(params) => (
							<CampoCliente
								params={params}
								option={selectedCliente}
								label="Cliente"
								sx={styles?.filterAutocomplete}
							/>
						)}
					/>

					{/* Toggle de Agendamento */}
					<Box
						sx={{ ...styles?.boxSwitch, p: 1 }}
						onClick={() => setIsAgendamentoEnabled((p) => !p)}
					>
						<Typography
							variant="body2"
							sx={{ color: "text.secondary", fontWeight: 600 }}
						>
							Agendamento
						</Typography>
						<Switch
							checked={isAgendamentoEnabled}
							onChange={() => setIsAgendamentoEnabled((p) => !p)}
							onClick={(e) => e.stopPropagation()}
							sx={styles?.switch}
							checkedIcon={<CheckCircle sx={styles?.iconSwitch} />}
							icon={<Cancel sx={styles?.iconSwitch} />}
							focusVisibleClassName=".Mui-focusVisible"
							disableRipple
						/>
					</Box>
				</Box>

			<Grid container spacing={2}>
				{/* Nome do Relatório */}
				<Grid item xs={12}>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
						<Typography variant="body2">Nome do Relatório</Typography>
						<TextField
							size="small"
							fullWidth
							value={nomeRelatorio}
							onChange={(e) => setNomeRelatorio(e.target.value)}
						/>
					</Box>
				</Grid>

				{/* Campos de Agendamento (visíveis apenas quando ativo) */}
				{isAgendamentoEnabled && (
					<>
						<Grid item xs={12} sm={6}>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
								<Typography
									variant="body2"
									sx={{ color: "text.secondary", fontWeight: 600 }}
								>
									Frequência de Agendamento
								</Typography>
								<TextField
									select
									size="small"
									fullWidth
									value={frequenciaAgendamento}
									onChange={(e) => setFrequenciaAgendamento(e.target.value)}
									SelectProps={{ native: false }}
								>
									{["Único", "Diário", "Semanal", "Mensal"].map((v) => (
										<MenuItem key={v} value={v}>
											{v}
										</MenuItem>
									))}
								</TextField>
								<Typography
									variant="caption"
									sx={{ color: "primary.main", fontWeight: 500, mt: 0.5 }}
								>
									{obterTextoAgendamento()}
								</Typography>
							</Box>
						</Grid>

						<Grid item xs={12} sm={6}>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
								<Typography
									variant="body2"
									sx={{ color: "text.secondary", fontWeight: 600 }}
								>
									{frequenciaAgendamento === "Único"
										? "Data de Envio"
										: "Data Inicial"}
								</Typography>
								<LocalizationProvider
									dateAdapter={AdapterDayjs}
									adapterLocale="pt-br"
								>
									<DateTimePicker
										format="DD/MM/YY HH:mm"
										ampm={false}
										value={agendamentoDataInicial ? dayjs(agendamentoDataInicial) : null}
										disablePast
										onChange={(v) => setAgendamentoDataInicial(v)}
										slotProps={{
											textField: { size: "small", fullWidth: true },
										}}
									/>
								</LocalizationProvider>
							</Box>
						</Grid>

						{/* Emails */}
						<Grid item xs={12}>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
								<Typography
									variant="body2"
									sx={{ color: "text.secondary", fontWeight: 600 }}
								>
									Emails para envio
								</Typography>
								{emails.map((email) => (
									<Box
										key={email.id}
										sx={{ display: "flex", gap: 1, alignItems: "center" }}
									>
										<TextField
											size="small"
											fullWidth
											placeholder="exemplo@email.com"
											value={email.value}
											onChange={(e) =>
												handleEmailChange(email.id, e.target.value)
											}
										/>
										<IconButton
											size="small"
											onClick={() => handleRemoveEmail(email.id)}
											disabled={emails.length === 1}
										>
											<Delete
												color={emails.length === 1 ? "disabled" : "error"}
												fontSize="small"
											/>
										</IconButton>
									</Box>
								))}
								<Button
									size="small"
									variant="outlined"
									startIcon={<Add />}
									onClick={handleAddEmail}
									sx={{ width: "fit-content", borderRadius: "8px", mt: 1 }}
								>
									Adicionar Email
								</Button>
							</Box>
						</Grid>
					</>
				)}
			</Grid>
		</Box>
	);
}
